import { box, type Box } from '../geometry/box';
import type { TrackKind } from '../tracking/track';

/**
 * The person's own picture on hidden faces: a cartoon head that covers the whole
 * face, or glasses, a moustache, a hat that sit on it. The face underneath is
 * always blurred first, so an accessory never leaves it recognisable.
 *
 * The face model gives a box, not landmarks, so accessories are placed from
 * where eyes, mouth and crown sit in that box (measured on the detector's
 * output, see `ANCHORS`). That is right for faces looking at the camera and
 * close for turned ones; tilted heads keep an upright accessory.
 */
export type StickerPlacement = 'cover' | 'eyes' | 'mouth' | 'head';
export const STICKER_PLACEMENTS: readonly StickerPlacement[] = ['cover', 'eyes', 'mouth', 'head'];

/** Which of the two the emoji style draws. */
export type StickerSource = 'emoji' | 'image';

/** A picture prepared on import: trimmed of its empty edges, made small, kept as a data URL so the recipe stays plain data. */
export interface StickerImage {
  /** `data:image/(png|webp|jpeg);base64,…` */
  readonly src: string;
  readonly width: number;
  readonly height: number;
}

/** Longest side a sticker is kept at: sharper than any face it lands on, small enough for recipes and presets. */
export const STICKER_MAX_SIDE = 512;
/** Upper bound for a stored sticker; anything bigger was not made by the importer. */
export const STICKER_MAX_SRC_LENGTH = 2_000_000;

const DATA_URL = /^data:image\/(png|webp|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/;

export function parseStickerImage(value: unknown): StickerImage | null {
  if (typeof value !== 'object' || value === null) return null;
  const { src, width, height } = value as Partial<Record<keyof StickerImage, unknown>>;
  const size = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 1 && n <= STICKER_MAX_SIDE * 4;
  if (typeof src !== 'string' || src.length > STICKER_MAX_SRC_LENGTH || !DATA_URL.test(src) || !size(width) || !size(height)) return null;
  return { src, width, height };
}

export const parseStickerPlacement = (value: unknown): StickerPlacement => (STICKER_PLACEMENTS.includes(value as StickerPlacement) ? (value as StickerPlacement) : 'cover');
export const parseStickerSource = (value: unknown): StickerSource => (value === 'image' ? 'image' : 'emoji');

/** The picture the emoji style should draw, or null for the emoji: a chosen picture that isn't there falls back to the emoji. */
export function activeStickerImage(privacy: { readonly sticker: StickerSource; readonly image: StickerImage | null }): StickerImage | null {
  return privacy.sticker === 'image' ? privacy.image : null;
}

/**
 * Where an accessory sits on a face box: its centre (share of the box) and
 * width (share of the box's width). `head` hangs from the crown instead of
 * being centred, so a hat sits on the hair whatever its height.
 */
const ANCHORS: Record<Exclude<StickerPlacement, 'cover'>, { readonly x: number; readonly y: number; readonly width: number }> = {
  eyes: { x: 0.5, y: 0.4, width: 1.25 },
  mouth: { x: 0.5, y: 0.74, width: 0.8 },
  head: { x: 0.5, y: -0.08, width: 1.4 },
};

/** How much larger than the mask a covering picture is drawn, so the mask's soft edge never shows around it (as for emoji). */
export const COVER_SCALE = 1.12;

/**
 * Where a sticker of `aspect` (width / height) is drawn.
 *
 * @param mask the padded area that is hidden
 * @param subject the found face itself, unpadded (accessories follow the face, not the margin)
 */
export function stickerRect(mask: Box, subject: Box, kind: TrackKind, placement: StickerPlacement, aspect: number): Box {
  const ratio = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  // Only faces have eyes: plates and drawn areas are always covered whole.
  if (placement === 'cover' || kind !== 'face') {
    const side = Math.max(mask.w, mask.h) * COVER_SCALE;
    const w = ratio >= 1 ? side : side * ratio;
    const h = ratio >= 1 ? side / ratio : side;
    return box(mask.x + (mask.w - w) / 2, mask.y + (mask.h - h) / 2, w, h);
  }
  const anchor = ANCHORS[placement];
  const w = subject.w * anchor.width;
  const h = w / ratio;
  const cx = subject.x + subject.w * anchor.x;
  const y = subject.y + subject.h * anchor.y;
  return box(cx - w / 2, placement === 'head' ? y - h : y - h / 2, w, h);
}

/**
 * The part of an RGBA picture that isn't (nearly) transparent, in pixels, or
 * null when nothing is visible. Lets a sticker's own empty margins not push
 * glasses off the eyes.
 */
export function opaqueBounds(rgba: Uint8ClampedArray, width: number, height: number, threshold = 8): Box | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      if (rgba[row + x * 4 + 3]! <= threshold) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  return maxX < 0 ? null : box(minX, minY, maxX - minX + 1, maxY - minY + 1);
}

/** A size shrunk (never grown) so its longest side is at most `max`. */
export function fitWithin(width: number, height: number, max = STICKER_MAX_SIDE): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** A sticker's bytes from its `src`, without fetching (a `data:` fetch can be blocked by a page's policy). */
export function stickerBlob(src: string): Blob {
  const comma = src.indexOf(',');
  const type = src.slice('data:'.length, src.indexOf(';'));
  const binary = atob(src.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}
